import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import settingsService from '@/shared/services/settingsService';

/**
 * The campus tutoring page the "also interested in" question links to.
 * Empty means no link is shown. Administrators only.
 */
function TutoringPageCard() {
    const [url, setUrl] = useState('');
    const [saved, setSaved] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const page = await settingsService.getTutoringPage();
            setSaved(page.url || '');
            setUrl(page.url || '');
        } catch (_err) {
            setLoadError('Failed to load the tutoring page link.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const inputId = 'tutoring-page-url';
    const unchanged = url.trim() === saved;

    const save = async (event) => {
        event.preventDefault();
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        setNotice('');
        try {
            const page = await settingsService.updateTutoringPage(url.trim());
            const stored = page?.url || '';
            setSaved(stored);
            setUrl(stored);
            setNotice(
                stored
                    ? `Students are now pointed to ${stored}.`
                    : 'The tutoring page link is cleared.'
            );
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    'Failed to save the tutoring page link.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the tutoring page link...
            </div>
        );
    }
    if (loadError) return <Alert variant="danger">{loadError}</Alert>;

    return (
        <Form onSubmit={save} noValidate>
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <h2 className="h5">Tutoring page</h2>
                    <p className="text-body-secondary">
                        The web page where students can learn about tutoring on
                        campus. The Learning Assistant application links to it
                        when it asks whether a student would also be interested
                        in being a tutor.
                    </p>
                    <Form.Group controlId={inputId} className="mb-3">
                        <Form.Label>Page address</Form.Label>
                        <Form.Control
                            type="url"
                            value={url}
                            placeholder="https://..."
                            onChange={(event) => setUrl(event.target.value)}
                        />
                    </Form.Group>
                    {saveError && (
                        <Alert variant="danger" role="alert">
                            {saveError}
                        </Alert>
                    )}
                    <div role="status">{notice}</div>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={saving || unchanged}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Card.Body>
            </Card>
        </Form>
    );
}

export default TutoringPageCard;
